import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Sparkles } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { fmtINR, fmtUSD } from "@/lib/money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const EMPTY = {
  name: "", description: "", price: 0, original_price: null,
  category: "Diyas", stock: 10, images: [], tags: [],
  is_featured: false, is_active: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  async function load() {
    const { data } = await api.get("/products", { params: { include_inactive: true } });
    setProducts(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing(null); setForm(EMPTY); setOpen(true);
  }
  function openEdit(p) {
    setEditing(p.id);
    setForm({
      name: p.name, description: p.description, price: p.price,
      original_price: p.original_price || null, category: p.category,
      stock: p.stock, images: p.images || [], tags: p.tags || [],
      is_featured: !!p.is_featured, is_active: p.is_active !== false,
    });
    setOpen(true);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        stock: parseInt(form.stock, 10),
        tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl text-[#2C1810]">Products</h1>
          <p className="font-body text-[#5B3D2A]">Manage your handicraft catalog.</p>
        </div>
        <Button data-testid="admin-add-product-btn" onClick={openNew} className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white btn-lift">
          <Plus className="w-4 h-4 mr-2" /> Add product
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#FFD1A6] shadow-warm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 font-body text-[#5B3D2A]">No products yet. Add your first festive piece.</TableCell></TableRow>
            )}
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`admin-row-${p.id}`}>
                <TableCell>
                  <div className="w-12 h-12 rounded-lg bg-[#FDEFDD] overflow-hidden">
                    {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                </TableCell>
                <TableCell className="font-body">
                  <div className="text-[#2C1810] font-semibold flex items-center gap-2">
                    {p.name}
                    {p.is_featured && <Sparkles className="w-3 h-3 text-[#FFB300]" />}
                  </div>
                </TableCell>
                <TableCell><span className="text-xs font-body text-[#4A148C]">{p.category}</span></TableCell>
                <TableCell className="font-body">
                  <div>₹{fmtINR(p.price)}</div>
                  <div className="text-[10px] text-[#5B3D2A]/70">≈ ${fmtUSD(p.price)}</div>
                </TableCell>
                <TableCell className="font-body">
                  <span className={p.stock < 5 ? "text-red-600 font-semibold" : ""}>{p.stock}</span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-1 rounded-full ${p.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                    {p.is_active ? "Active" : "Hidden"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="text-[#4A148C] hover:text-[#E65C00] p-2">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="text-red-700 hover:text-red-900 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#2C1810]">{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Name</Label>
              <Input data-testid="prod-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea data-testid="prod-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Category</Label>
              <Input data-testid="prod-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Tags (comma separated)</Label>
              <Input data-testid="prod-tags" value={Array.isArray(form.tags) ? form.tags.join(",") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Price (₹)</Label>
              <Input data-testid="prod-price" type="number" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Original price (₹, optional)</Label>
              <Input data-testid="prod-orig" type="number" step="1" value={form.original_price ?? ""} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Stock</Label>
              <Input data-testid="prod-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-center gap-6 pt-6">
              <div className="flex items-center gap-2">
                <Switch data-testid="prod-featured" checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} />
                <span className="font-body text-sm">Festive Pick</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="prod-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="font-body text-sm">Active</span>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label>Images</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {form.images.map((path, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#FFD1A6]">
                    <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })}
                      className="absolute top-1 right-1 bg-red-600 text-white w-5 h-5 rounded-full flex items-center justify-center"
                    ><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <button
                  data-testid="prod-upload-btn"
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-24 h-24 rounded-xl border-2 border-dashed border-[#FFD1A6] flex items-center justify-center text-[#4A148C] hover:bg-[#FFF3E0]"
                >
                  <Upload className="w-5 h-5" />
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
              </div>
              {uploading && <div className="text-xs text-[#4A148C] mt-2">Uploading…</div>}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="prod-cancel" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="prod-save" onClick={save} disabled={saving} className="bg-[#E65C00] hover:bg-[#C44A00] text-white rounded-full">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create product")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
